'use es6';
/*
 * Adds HubL validation to CodeMirror instance.
 *
 * Requires @decorator/annotations
 */

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'react-utils/debounce';
import getComponentName from 'react-utils/getComponentName';
import { createValidateClient } from 'react-codemirror/util/HublValidation';
import { isEqual } from '../util/miniUnderscore.js';
const VALIDATE_DEBOUNCE_PERIOD = 500;
const EMPTY_ANNOTATIONS = Object.freeze([]);

function createAnnotations(propAnnotations, hublAnnotations) {
  if (propAnnotations.length === 0 && hublAnnotations.length === 0) {
    return EMPTY_ANNOTATIONS;
  }

  return [].concat(propAnnotations, hublAnnotations);
}

export default function hublValidation() {
  return function withHublValidation(CodemirrorComponent) {
    /**
     * @class
     */
    class WithHublValidation extends Component {
      constructor(props) {
        super(props);
        this.hublValidate = createValidateClient();
        this.currentValue = props.value;
        this.propAnnotations = props.annotations || EMPTY_ANNOTATIONS;
        this.hublAnnotations = EMPTY_ANNOTATIONS;
        this.state = {
          annotations: createAnnotations(this.propAnnotations, this.hublAnnotations)
        };
        this.validate = this.validate.bind(this);
        this.validateDebounced = debounce(this.validate, VALIDATE_DEBOUNCE_PERIOD);
        this.onHublValidation = this.onHublValidation.bind(this);
        this.onHublValidationFail = this.onHublValidationFail.bind(this);
        this.onChange = this.onChange.bind(this);
      }

      componentDidMount() {
        const {
          props
        } = this;

        if (props.value) {
          this.validate(props.value, props);
        }
      }

      componentDidUpdate(prevProps) {
        const {
          props
        } = this;

        if (this.currentValue !== props.value) {
          this.currentValue = props.value;
          this.validateDebounced(props.value, props);
        } else if (prevProps.hublValidationOptions !== props.hublValidationOptions && !isEqual(prevProps.hublValidationOptions, props.hublValidationOptions)) {
          this.validateDebounced(this.currentValue, props);
        }

        if (props.annotations !== prevProps.annotations && !isEqual(props.annotations, prevProps.annotations)) {
          this.propAnnotations = props.annotations && props.annotations.length ? props.annotations : EMPTY_ANNOTATIONS;
          const annotations = createAnnotations(this.propAnnotations, this.hublAnnotations);
          this.setState({
            annotations
          });
        }
      }

      componentWillUnmount() {
        this.hublValidate.cancel();
      }

      validate(value, props) {
        if (!props.enableHublValidation) return;
        this.hublValidate(value, props.hublValidationOptions, this.onHublValidation, this.onHublValidationFail);
      }

      onHublValidation({
        annotations,
        response
      }) {
        if (!this.props.enableHublValidation) return;

        if (this.props.onHublValidation) {
          this.props.onHublValidation(annotations, response);
        }

        if (annotations.length === 0 && this.hublAnnotations.length === 0) {
          return;
        }

        if (isEqual(annotations, this.hublAnnotations)) {
          return;
        }

        this.hublAnnotations = annotations.length ? annotations : EMPTY_ANNOTATIONS;
        const mergedAnnotations = createAnnotations(this.propAnnotations, this.hublAnnotations);
        this.setState({
          annotations: mergedAnnotations
        });
      }

      onHublValidationFail({
        error
      }) {
        if (!this.props.enableHublValidation) return;

        if (this.props.onHublValidationFail) {
          this.props.onHublValidationFail(error);
        }
      }

      onChange(data) {
        const {
          props
        } = this;
        this.currentValue = data.value;
        this.validateDebounced(data.value, props);

        if (props.onChange) {
          props.onChange(data);
        }
      }

      render() {
        return /*#__PURE__*/_jsx(CodemirrorComponent, Object.assign({}, this.props, {
          annotations: this.state.annotations,
          onChange: this.onChange
        }));
      }

    }
    /*
     * Statics
     */


    WithHublValidation.propTypes = {
      value: PropTypes.string,
      annotations: PropTypes.array,

      /**
       * Enables/Disables validation
       */
      enableHublValidation: PropTypes.bool,

      /**
       * Optional data to be posted to validation API
       */
      hublValidationOptions: PropTypes.shape({
        isAvailableForNewContent: PropTypes.bool,
        isHeadHtml: PropTypes.bool,
        moduleContentTypes: PropTypes.arrayOf(PropTypes.string),
        moduleHostTemplateTypes: PropTypes.arrayOf(PropTypes.string),
        templateType: PropTypes.number,
        templateId: PropTypes.number,
        templatePath: PropTypes.string
      }),
      onChange: PropTypes.func,

      /**
       * Optional callback fired on response from API.
       * (annotations, apiResponse) => { }
       */
      onHublValidation: PropTypes.func,

      /**
       * Optional callback fired on error response from API.
       * (apiResponse) => { }
       */
      onHublValidationFail: PropTypes.func
    };
    WithHublValidation.defaultProps = {
      enableHublValidation: true,
      hublValidationOptions: {}
    };
    WithHublValidation.displayName = `WithHublValidation(${getComponentName(CodemirrorComponent)})`;
    return WithHublValidation;
  };
}